import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { AxiosError } from "axios";

import { useSnackbar } from "@/components/Snackbar";
import { OfferingLinkBox } from "@/components/Segment/OfferingLinkBox";
import { SegmentScoringCard } from "@/components/Segment/SegmentScoringCard";
import {
  createWorkbookFromSegment,
  CreateWorkbookFromSegmentBody,
  CreatedWorkbook,
} from "@/apis/emback/workbook";
import {
  fetchSalesCopilotOfferings,
  SalesCopilotOffering,
} from "@/apis/emback/offerings";
import type { ScoringModel } from "@/apis/argo/scoringModels";
import env from "@/utils/env";

interface SaveAsWorkbookDialogProps {
  open: boolean;
  /** Prefill: current segment name (or empty for unsaved filter sets). */
  initialName?: string;
  /** Everything but the name and offering, composed by the page from its live query vars. */
  payload: Omit<CreateWorkbookFromSegmentBody, "name" | "offeringId">;
  /** How many companies the list will hold; the copy stays generic while unknown. */
  companyCount?: number;
  /** The V2 models the segment is scored by, which the list keeps in Sales Copilot. */
  models?: ScoringModel[];
  /** Whether the user may choose the offering the list opens on (an admin). */
  canLinkOffering?: boolean;
  onClose: () => void;
}

const NO_MODELS: ScoringModel[] = [];

/**
 * The dialog's lead sentence, naming how many companies the list will hold
 * once the count is known.
 *
 * @param companyCount - The segment's company count, if known.
 * @returns The sentence.
 *
 * @example
 * ```typescript
 * savesCopy(14);
 * ```
 */
function savesCopy(companyCount: number | undefined): string {
  if (companyCount == null) {
    return "Saves the companies currently matching this segment as a list in AI Sales Copilot.";
  }
  const companies =
    companyCount === 1
      ? "1 company"
      : `${companyCount.toLocaleString()} companies`;
  return `Saves the ${companies} matching this segment as a list in AI Sales Copilot.`;
}

/**
 * Loads the tenant's Sales Copilot offerings once, while `enabled`. A failed
 * load reads as no offerings, so the admin box only disables its button.
 *
 * @param enabled - Whether the offerings are needed.
 * @returns The offerings, or null until they load.
 */
function useSalesCopilotOfferings(
  enabled: boolean
): SalesCopilotOffering[] | null {
  const [offerings, setOfferings] = useState<SalesCopilotOffering[] | null>(
    null
  );
  useEffect(() => {
    if (!enabled || offerings) return;
    let cancelled = false;
    const loadOfferings = async () => {
      try {
        const data = await fetchSalesCopilotOfferings();
        if (!cancelled) setOfferings(data);
      } catch {
        if (!cancelled) setOfferings([]);
      }
    };
    void loadOfferings();
    return () => {
      cancelled = true;
    };
  }, [enabled, offerings]);
  return offerings;
}

/**
 * "Add to Sales Copilot": saves the segment's companies as a list in AI Sales
 * Copilot, keeping the V2 models the segment is scored by. An admin can also
 * choose the offering the list opens on there.
 *
 * @param props - See {@link SaveAsWorkbookDialogProps}.
 */
export function SaveAsWorkbookDialog({
  open,
  initialName,
  payload,
  companyCount,
  models = NO_MODELS,
  canLinkOffering = false,
  onClose,
}: SaveAsWorkbookDialogProps) {
  const snackbar = useSnackbar();
  const [name, setName] = useState(initialName ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [created, setCreated] = useState<CreatedWorkbook | null>(null);
  const [linkedOfferingId, setLinkedOfferingId] = useState<string | null>(null);
  const showOfferingLink = canLinkOffering && models.length > 0;
  const offerings = useSalesCopilotOfferings(open && showOfferingLink);

  useEffect(() => {
    if (open) {
      setName(initialName ?? "");
      setCreated(null);
      setLinkedOfferingId(null);
    }
  }, [open, initialName]);

  const handleSave = useCallback(async () => {
    if (!name.trim()) return;
    setIsSaving(true);
    try {
      const workbook = await createWorkbookFromSegment({
        ...payload,
        name: name.trim(),
        ...(linkedOfferingId ? { offeringId: linkedOfferingId } : {}),
      });
      setCreated(workbook);
    } catch (err) {
      console.error("Failed to save segment as workbook:", err);
      const detail =
        err instanceof AxiosError &&
        typeof err.response?.data?.message === "string"
          ? err.response.data.message
          : undefined;
      snackbar.showError(
        detail ?? "Failed to save segment as list. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  }, [name, payload, linkedOfferingId, snackbar]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      id="save-as-workbook-dialog"
      data-testid="save-as-workbook-dialog"
    >
      <DialogTitle data-testid="save-as-workbook-title">
        Add to Sales Copilot
      </DialogTitle>
      {created ? (
        <>
          <DialogContent>
            <Typography>
              List &ldquo;{created.name}&rdquo; created
              {created.segmentSnapshot
                ? ` with ${created.segmentSnapshot.rowCount.toLocaleString()} companies`
                : ""}
              . It is ready to use in AI Sales Copilot.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ padding: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={onClose}
              data-testid="save-as-workbook-close-btn"
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              endIcon={<OpenInNewIcon />}
              href={`${env.HSI_URL}/workbooks/${created.id}`}
              target="_blank"
              rel="noopener noreferrer"
              id="save-as-workbook-open-btn"
              data-testid="save-as-workbook-open-btn"
            >
              Open in AI Sales Copilot
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2.5 }}
              data-testid="save-as-workbook-description"
            >
              {savesCopy(companyCount)}
            </Typography>
            <Typography
              component="label"
              htmlFor="save-as-workbook-name-input"
              variant="subtitle2"
              sx={{ display: "block", mb: 0.75 }}
            >
              List name
            </Typography>
            <TextField
              id="save-as-workbook-name-input"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              inputProps={{
                maxLength: 255,
                "data-testid": "save-as-workbook-name-input",
              }}
            />
            {models.length > 0 && (
              <>
                <Typography
                  variant="overline"
                  component="h3"
                  color="text.secondary"
                  sx={{ display: "block", mt: 3, mb: 1 }}
                >
                  Scoring in Sales Copilot
                </Typography>
                <SegmentScoringCard
                  models={models}
                  modelScoreFilter={payload.modelScoreFilter}
                />
                {showOfferingLink && (
                  <OfferingLinkBox
                    offerings={offerings}
                    linkedOfferingId={linkedOfferingId}
                    onLink={setLinkedOfferingId}
                  />
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ padding: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={onClose}
              disabled={isSaving}
              data-testid="save-as-workbook-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!name.trim() || isSaving}
              id="save-as-workbook-save-btn"
              data-testid="save-as-workbook-save-btn"
            >
              {isSaving ? "Saving..." : "Save to Sales Copilot"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
